import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useAuth } from '../../../context/AuthContext';
import { invalidateClassContextCache } from '../../../components/common/ClassContextPicker';

const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'IGCSE', 'Matriculation', 'Other'];
const MEDIUMS = ['English', 'Tamil', 'Hindi', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Gujarati', 'Other'];
const SCHOOL_TYPES = ['Primary School', 'Middle School', 'High School', 'Higher Secondary', 'Primary + High School', 'K-12 School', 'International School', 'College', 'University'];
const ACADEMIC_STARTS = ['January', 'April', 'June', 'July', 'September'];
const BRANCH_TYPES = ['Main Campus', 'Primary Wing', 'Middle School Wing', 'Secondary Wing', 'Higher Secondary Wing', 'Other'];

const blank = {
  school_name: '', school_code: '', school_type: '', board: '', medium: '',
  principal_name: '', contact_email: '', contact_phone: '', website: '',
  established_year: '', address: '', city: '', state: '', pincode: '',
  total_classes: '', academic_year_start: 'April',
  classes: [],    // Array of class/grade names
  sections: [],   // Array of section labels
  subjects: [],   // Array of subject names
};

const branchBlank = { name: '', type: 'Main Campus', address: '', phone: '', head_name: '' };

export default function SchoolConfigPage() {
  const { refreshTenant } = useAuth();
  const [record, setRecord] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [form, setForm] = useState(blank);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [branchForm, setBranchForm] = useState(branchBlank);
  const [editBranchIdx, setEditBranchIdx] = useState(null);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [newClass, setNewClass] = useState('');
  const [newSection, setNewSection] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/records/school_config', { params: { limit: 1 } });
      const rec = (r.data?.records || [])[0];
      if (rec) {
        setRecord(rec);
        setRecordId(rec._id);
        const rd = rec.recordData || {};
        setForm({
          ...blank,
          ...rd,
          // Always ensure these are proper arrays (MongoDB Mixed can return null)
          classes:  Array.isArray(rd.classes)  ? rd.classes  : [],
          sections: Array.isArray(rd.sections) ? rd.sections : [],
          subjects: Array.isArray(rd.subjects) ? rd.subjects : [],
        });
        setBranches(Array.isArray(rd.branches) ? rd.branches : []);
      }
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const data = { ...form, branches };
      if (recordId) await api.put(`/records/school_config/${recordId}`, data);
      else await api.post('/records/school_config', data);
      setToast({ show: true, message: 'School configuration saved!', type: 'success' });
      // Backend mirrors school_name → tenant.name (and contact/website/address)
      // when school_config is saved. Refresh the tenant context so the topbar,
      // dashboard greeting, etc. pick up the new name without a page reload.
      if (refreshTenant) refreshTenant().catch(() => {});
      // Bust the ClassContextPicker cache so newly-added classes / sections /
      // subjects appear in dropdowns across the app immediately, without the
      // user needing to hard-reload.
      invalidateClassContextCache();
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to save', type: 'danger' });
    } finally { setSaving(false); }
  };

  const addBranch = () => {
    if (!branchForm.name) return;
    if (editBranchIdx !== null) {
      setBranches(b => b.map((x, i) => i === editBranchIdx ? { ...branchForm } : x));
      setEditBranchIdx(null);
    } else {
      setBranches(b => [...b, { ...branchForm }]);
    }
    setBranchForm(branchBlank);
    setShowBranchForm(false);
  };

  const removeBranch = (idx) => { setBranches(b => b.filter((_, i) => i !== idx)); };
  const editBranch = (idx) => { setBranchForm({ ...branches[idx] }); setEditBranchIdx(idx); setShowBranchForm(true); };

  const addClass = () => {
    const v = newClass.trim();
    if (!v || (form.classes || []).includes(v)) return;
    f('classes', [...(form.classes || []), v]);
    setNewClass('');
  };
  const removeClass = (idx) => f('classes', (form.classes || []).filter((_, i) => i !== idx));

  const addSection = () => {
    const v = newSection.trim().toUpperCase();
    if (!v || (form.sections || []).includes(v)) return;
    f('sections', [...(form.sections || []), v]);
    setNewSection('');
  };
  const removeSection = (idx) => f('sections', (form.sections || []).filter((_, i) => i !== idx));

  const addSubject = () => {
    const v = newSubject.trim();
    if (!v || (form.subjects || []).includes(v)) return;
    f('subjects', [...(form.subjects || []), v]);
    setNewSubject('');
  };
  const removeSubject = (idx) => f('subjects', (form.subjects || []).filter((_, i) => i !== idx));

  const TABS = [
    { key: 'general', label: 'School Info', icon: 'bi-building' },
    { key: 'contact', label: 'Contact & Address', icon: 'bi-geo-alt' },
    { key: 'academic', label: 'Academic Settings', icon: 'bi-journal-bookmark' },
    { key: 'branches', label: `Branches / Campuses ${branches.length > 0 ? `(${branches.length})` : ''}`, icon: 'bi-buildings' },
  ];

  if (loading) return <LoadingSpinner fullPage text="Loading school configuration…" />;

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h4 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>
            <i className="bi bi-building me-2 text-primary" />School Configuration
          </h4>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {record ? `Last updated: ${new Date(record.updatedAt || record.createdAt).toLocaleDateString()}` : 'Configure your school details'}
          </div>
        </div>
        {record && (
          <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
            <i className="bi bi-check-circle me-1" />Configured
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="card mb-4">
        <div className="card-body p-0">
          <div className="d-flex border-bottom" style={{ overflowX: 'auto' }}>
            {TABS.map(t => (
              <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                style={{ border: 'none', background: 'none', padding: '13px 18px', fontWeight: 600, fontSize: 13,
                  borderBottom: activeTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
                  color: activeTab === t.key ? '#6366f1' : '#64748b', whiteSpace: 'nowrap', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                <i className={`bi ${t.icon}`} />{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="row g-3">
          {/* ── Left: form fields ─────────────────────────────────── */}
          <div className="col-lg-8">

            {/* General Info */}
            {activeTab === 'general' && (
              <div className="card">
                <div className="card-header fw-semibold"><i className="bi bi-building me-2 text-primary"></i>School Information</div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">School Name <span className="text-danger">*</span></label>
                      <input className="form-control form-control-lg" value={form.school_name} onChange={e => f('school_name', e.target.value)} required placeholder="e.g. St. Mary's Higher Secondary School" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">School Code / Affiliation No.</label>
                      <input className="form-control" value={form.school_code} onChange={e => f('school_code', e.target.value)} placeholder="e.g. TN1234" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Established Year</label>
                      <input type="number" className="form-control" value={form.established_year} onChange={e => f('established_year', e.target.value)} placeholder="e.g. 1985" min="1800" max="2025" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">School Type <span className="text-danger">*</span></label>
                      <select className="form-select" value={form.school_type} onChange={e => f('school_type', e.target.value)} required>
                        <option value="">Select type...</option>
                        {SCHOOL_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Board / Affiliation</label>
                      <select className="form-select" value={form.board} onChange={e => f('board', e.target.value)}>
                        <option value="">Select board...</option>
                        {BOARDS.map(b => <option key={b}>{b}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Medium of Instruction</label>
                      <select className="form-select" value={form.medium} onChange={e => f('medium', e.target.value)}>
                        <option value="">Select medium...</option>
                        {MEDIUMS.map(m => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Principal Name</label>
                      <input className="form-control" value={form.principal_name} onChange={e => f('principal_name', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact */}
            {activeTab === 'contact' && (
              <div className="card">
                <div className="card-header fw-semibold"><i className="bi bi-geo-alt me-2 text-primary"></i>Contact & Address</div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Contact Email</label>
                      <input type="email" className="form-control" value={form.contact_email} onChange={e => f('contact_email', e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Contact Phone</label>
                      <input className="form-control" value={form.contact_phone} onChange={e => f('contact_phone', e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Website</label>
                      <input type="url" className="form-control" value={form.website} onChange={e => f('website', e.target.value)} placeholder="https://" />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Full Address</label>
                      <textarea className="form-control" rows={3} value={form.address} onChange={e => f('address', e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">City</label>
                      <input className="form-control" value={form.city} onChange={e => f('city', e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">State</label>
                      <input className="form-control" value={form.state} onChange={e => f('state', e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Pincode</label>
                      <input className="form-control" value={form.pincode} onChange={e => f('pincode', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Academic Settings */}
            {activeTab === 'academic' && (
              <div className="card">
                <div className="card-header fw-semibold"><i className="bi bi-journal-bookmark me-2 text-primary"></i>Academic Settings</div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Academic Year Start</label>
                      <select className="form-select" value={form.academic_year_start} onChange={e => f('academic_year_start', e.target.value)}>
                        {ACADEMIC_STARTS.map(m => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">No. of Labs</label>
                      <input type="number" className="form-control" value={form.num_labs || ''} onChange={e => f('num_labs', e.target.value)} min="0" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Library Present?</label>
                      <select className="form-select" value={form.has_library || ''} onChange={e => f('has_library', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">No. of Students (approx)</label>
                      <select className="form-select" value={form.student_count_range || ''} onChange={e => f('student_count_range', e.target.value)}>
                        <option value="">Select range...</option>
                        {['1-100', '101-300', '301-500', '501-1000', '1001-2000', '2000+'].map(r => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Sports Facility?</label>
                      <select className="form-select" value={form.has_sports || ''} onChange={e => f('has_sports', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>

                    {/* Classes / Grades Management */}
                    <div className="col-12 mt-2">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label mb-0 fw-semibold">
                          <i className="bi bi-mortarboard me-1 text-primary"></i>
                          Class / Grade Names
                          <span className="text-muted ms-2" style={{ fontWeight: 400, fontSize: 12 }}>— used across Student, Exams, Timetable, Attendance</span>
                        </label>
                        <span className="badge bg-primary bg-opacity-10 text-primary">{(form.classes || []).length} classes</span>
                      </div>
                      <div className="input-group input-group-sm mb-2">
                        <input
                          className="form-control"
                          placeholder="e.g. Class 6 — Section A  or  Pre-Primary (LKG)"
                          value={newClass}
                          onChange={e => setNewClass(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addClass(); } }}
                        />
                        <button type="button" className="btn btn-primary btn-sm" onClick={addClass}>
                          <i className="bi bi-plus-lg me-1"></i>Add Class
                        </button>
                      </div>
                      <div className="d-flex flex-wrap gap-1 p-2 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', minHeight: 44 }}>
                        {(form.classes || []).length === 0 ? (
                          <span className="text-muted" style={{ fontSize: 12 }}>No classes added yet. Type a class name above and click Add Class.</span>
                        ) : (form.classes || []).map((c, i) => (
                          <span key={i} className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded" style={{ background: '#ede9fe', color: '#6366f1', fontSize: 12, fontWeight: 500 }}>
                            {c}
                            <button type="button" onClick={() => removeClass(i)}
                              style={{ background: 'none', border: 'none', color: '#6366f1', padding: 0, cursor: 'pointer', fontSize: 15, lineHeight: 1, fontWeight: 700 }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                        <i className="bi bi-info-circle me-1"></i>Add each class individually. These will appear as dropdown options in all student-related modules.
                      </div>
                    </div>

                    {/* Sections Management */}
                    <div className="col-12 mt-2">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label mb-0 fw-semibold">
                          <i className="bi bi-grid-3x3-gap me-1 text-warning"></i>
                          Sections
                          <span className="text-muted ms-2" style={{ fontWeight: 400, fontSize: 12 }}>— e.g. A, B, C used across Attendance, Timetable, Students</span>
                        </label>
                        <span className="badge bg-warning bg-opacity-10 text-warning">{(form.sections || []).length} sections</span>
                      </div>
                      {/* Quick-add preset sections */}
                      <div className="d-flex gap-1 flex-wrap mb-2">
                        <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Quick add:</span>
                        {['A','B','C','D','E','Science','Commerce','Arts'].map(s => (
                          <button key={s} type="button"
                            onClick={() => { if (!(form.sections || []).includes(s)) f('sections', [...(form.sections || []), s]); }}
                            disabled={(form.sections || []).includes(s)}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, border: '1px solid #fbbf24', background: (form.sections || []).includes(s) ? '#fef3c7' : '#fff', color: '#92400e', cursor: (form.sections || []).includes(s) ? 'default' : 'pointer', opacity: (form.sections || []).includes(s) ? 0.5 : 1 }}>
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="input-group input-group-sm mb-2">
                        <input
                          className="form-control"
                          placeholder="Type custom section name and press Enter"
                          value={newSection}
                          onChange={e => setNewSection(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSection(); } }}
                        />
                        <button type="button" className="btn btn-warning btn-sm" onClick={addSection}>
                          <i className="bi bi-plus-lg me-1"></i>Add
                        </button>
                      </div>
                      <div className="d-flex flex-wrap gap-1 p-2 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', minHeight: 44 }}>
                        {(form.sections || []).length === 0 ? (
                          <span className="text-muted" style={{ fontSize: 12 }}>No sections added yet. Use the quick-add buttons above or type a custom name.</span>
                        ) : (form.sections || []).map((s, i) => (
                          <span key={i} className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded" style={{ background: '#fef9c3', color: '#854d0e', fontSize: 12, fontWeight: 500 }}>
                            {s}
                            <button type="button" onClick={() => removeSection(i)}
                              style={{ background: 'none', border: 'none', color: '#854d0e', padding: 0, cursor: 'pointer', fontSize: 15, lineHeight: 1, fontWeight: 700 }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                        <i className="bi bi-info-circle me-1"></i>Sections appear as dropdown options in Students, Attendance, Timetable, and Exams modules.
                      </div>
                    </div>

                    {/* Subjects Management */}
                    <div className="col-12 mt-2">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <label className="form-label mb-0 fw-semibold">
                          <i className="bi bi-book me-1 text-primary"></i>
                          Subjects
                          <span className="text-muted ms-2" style={{ fontWeight: 400, fontSize: 12 }}>— used in Exams, Timetable, Syllabus Tracking</span>
                        </label>
                        <span className="badge bg-success bg-opacity-10 text-success">{(form.subjects || []).length} subjects</span>
                      </div>
                      {/* Quick-add preset subjects */}
                      <div className="d-flex gap-1 flex-wrap mb-2">
                        <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Quick add:</span>
                        {['Mathematics','Science','English','Social Studies','Hindi','Tamil','Computer Science','Physics','Chemistry','Biology','History','Geography','Economics','Accountancy','Business Studies','Physical Education'].map(s => (
                          <button key={s} type="button"
                            onClick={() => { if (!(form.subjects || []).includes(s)) f('subjects', [...(form.subjects || []), s]); }}
                            disabled={(form.subjects || []).includes(s)}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, border: '1px solid #86efac', background: (form.subjects || []).includes(s) ? '#dcfce7' : '#fff', color: '#166534', cursor: (form.subjects || []).includes(s) ? 'default' : 'pointer', opacity: (form.subjects || []).includes(s) ? 0.5 : 1 }}>
                            {s}
                          </button>
                        ))}
                      </div>
                      <div className="input-group input-group-sm mb-2">
                        <input
                          className="form-control"
                          placeholder="Type a custom subject name and press Enter"
                          value={newSubject}
                          onChange={e => setNewSubject(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubject(); } }}
                        />
                        <button type="button" className="btn btn-success btn-sm" onClick={addSubject}>
                          <i className="bi bi-plus-lg me-1"></i>Add Subject
                        </button>
                      </div>
                      <div className="d-flex flex-wrap gap-1 p-2 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', minHeight: 44 }}>
                        {(form.subjects || []).length === 0 ? (
                          <span className="text-muted" style={{ fontSize: 12 }}>No subjects added yet. Type a subject name and click Add Subject.</span>
                        ) : (form.subjects || []).map((s, i) => (
                          <span key={i} className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded" style={{ background: '#dcfce7', color: '#16a34a', fontSize: 12, fontWeight: 500 }}>
                            {s}
                            <button type="button" onClick={() => removeSubject(i)}
                              style={{ background: 'none', border: 'none', color: '#16a34a', padding: 0, cursor: 'pointer', fontSize: 15, lineHeight: 1, fontWeight: 700 }}>×</button>
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                        <i className="bi bi-info-circle me-1"></i>Add each subject individually. These will appear as dropdown options in Exams, Timetable, and other modules.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}</div>

          {/* ── Right: summary / info panel ──────────────────────── */}
          {activeTab !== 'branches' && (
            <div className="col-lg-4">
              {/* School summary card */}
              <div className="card mb-3" style={{ borderRadius: 12, border: '2px solid #e0e7ff' }}>
                <div className="card-body text-center py-4">
                  <div className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3"
                    style={{ width: 64, height: 64, background: '#ede9fe', fontSize: 28 }}>
                    <i className="bi bi-building text-primary"></i>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>
                    {form.school_name || <span className="text-muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>School name not set</span>}
                  </div>
                  {form.school_code && (
                    <div><code style={{ fontSize: 12, background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>{form.school_code}</code></div>
                  )}
                  <div className="mt-2 d-flex flex-wrap gap-1 justify-content-center">
                    {form.board     && <span className="badge bg-primary bg-opacity-10 text-primary">{form.board}</span>}
                    {form.medium    && <span className="badge bg-success bg-opacity-10 text-success">{form.medium}</span>}
                    {form.school_type && <span className="badge bg-info bg-opacity-10 text-info">{form.school_type}</span>}
                  </div>
                </div>
              </div>

              {/* Quick info list */}
              <div className="card" style={{ borderRadius: 12 }}>
                <div className="card-header fw-semibold" style={{ fontSize: 13 }}>
                  <i className="bi bi-info-circle me-2 text-primary"></i>Configuration Summary
                </div>
                <ul className="list-group list-group-flush" style={{ fontSize: 12 }}>
                  {[
                    { icon: 'bi-person-badge', label: 'Principal', value: form.principal_name },
                    { icon: 'bi-geo-alt', label: 'City', value: form.city },
                    { icon: 'bi-telephone', label: 'Phone', value: form.contact_phone },
                    { icon: 'bi-envelope', label: 'Email', value: form.contact_email },
                    { icon: 'bi-calendar', label: 'Est.', value: form.established_year },
                    { icon: 'bi-mortarboard', label: 'Classes', value: (form.classes?.length > 0) ? `${form.classes.length} grades` : (form.total_classes ? `${form.total_classes} grades` : null) },
                    { icon: 'bi-people', label: 'Students', value: form.student_count_range },
                    { icon: 'bi-buildings', label: 'Branches', value: branches.length > 0 ? `${branches.length} campus(es)` : null },
                  ].filter(i => i.value).map((item, idx) => (
                    <li key={idx} className="list-group-item d-flex align-items-center gap-2 py-2 px-3">
                      <i className={`bi ${item.icon} text-muted`} style={{ width: 16 }}></i>
                      <span className="text-muted">{item.label}:</span>
                      <span className="fw-medium ms-auto">{item.value}</span>
                    </li>
                  ))}
                  {!form.school_name && !form.city && (
                    <li className="list-group-item text-center text-muted py-3" style={{ fontSize: 12 }}>
                      Fill in the form to see a summary here
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}

        {/* Branches */}
        {activeTab === 'branches' && (
          <div>
            <div className="alert alert-info d-flex gap-2 align-items-start mb-3" style={{ fontSize: 13, borderRadius: 10 }}>
              <i className="bi bi-info-circle-fill mt-1" />
              <div>
                <strong>Multi-Campus Support</strong> — If your school has multiple branches (e.g. Primary Wing, Secondary Wing), add them here. All branches share the same login and data but can be filtered independently. Branch limit is based on your subscription plan.
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-header d-flex align-items-center justify-content-between">
                <span className="fw-semibold">Branches / Campuses ({branches.length})</span>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => { setBranchForm(branchBlank); setEditBranchIdx(null); setShowBranchForm(s => !s); }}>
                  <i className="bi bi-plus-lg me-1" />Add Branch
                </button>
              </div>

              {showBranchForm && (
                <div className="card-body border-bottom" style={{ background: '#f8f9ff' }}>
                  <div className="row g-3">
                    <div className="col-md-5"><label className="form-label">Branch Name <span className="text-danger">*</span></label>
                      <input className="form-control" value={branchForm.name} onChange={e => setBranchForm(b => ({ ...b, name: e.target.value }))} placeholder="e.g. Primary Wing" />
                    </div>
                    <div className="col-md-4"><label className="form-label">Branch Type</label>
                      <select className="form-select" value={branchForm.type} onChange={e => setBranchForm(b => ({ ...b, type: e.target.value }))}>
                        {BRANCH_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-3"><label className="form-label">Head / In-charge</label>
                      <input className="form-control" value={branchForm.head_name} onChange={e => setBranchForm(b => ({ ...b, head_name: e.target.value }))} />
                    </div>
                    <div className="col-md-5"><label className="form-label">Address (if different)</label>
                      <input className="form-control" value={branchForm.address} onChange={e => setBranchForm(b => ({ ...b, address: e.target.value }))} />
                    </div>
                    <div className="col-md-3"><label className="form-label">Phone</label>
                      <input className="form-control" value={branchForm.phone} onChange={e => setBranchForm(b => ({ ...b, phone: e.target.value }))} />
                    </div>
                    <div className="col-md-4 d-flex align-items-end gap-2">
                      <button type="button" className="btn btn-primary btn-sm" onClick={addBranch}>{editBranchIdx !== null ? 'Update' : 'Add'} Branch</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowBranchForm(false); setEditBranchIdx(null); }}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body p-0">
                {branches.length === 0 ? (
                  <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
                    <i className="bi bi-buildings d-block mb-2" style={{ fontSize: 28 }} />
                    No branches added yet. Click "Add Branch" to add a campus.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr>
                          <th style={{ padding: '10px 16px' }}>Branch Name</th>
                          <th>Type</th>
                          <th>In-charge</th>
                          <th>Phone</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {branches.map((b, i) => (
                          <tr key={i}>
                            <td style={{ padding: '10px 16px', fontWeight: 600 }}>
                              {i === 0 && <span className="badge bg-primary me-2" style={{ fontSize: 9 }}>Main</span>}
                              {b.name}
                            </td>
                            <td><span className="badge bg-light text-dark">{b.type}</span></td>
                            <td>{b.head_name || '—'}</td>
                            <td>{b.phone || '—'}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <button type="button" className="btn btn-outline-primary btn-sm" style={{ padding: '2px 8px' }} onClick={() => editBranch(i)}><i className="bi bi-pencil" /></button>
                                <button type="button" className="btn btn-outline-danger btn-sm" style={{ padding: '2px 8px' }} onClick={() => removeBranch(i)}><i className="bi bi-trash" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>{/* end row g-3 */}

        {/* Save Button */}
        <div className="d-flex justify-content-end mt-3 gap-2">
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ minWidth: 140 }}>
            {saving && <span className="spinner-border spinner-border-sm me-2" />}
            <i className="bi bi-check-lg me-1" />{recordId ? 'Update Configuration' : 'Save Configuration'}
          </button>
        </div>
      </form>

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
